/**
 * ERP record-field-write API client.
 *
 * Served by the ERP domain rather than MDM — MDM must not learn to talk to
 * the ERP. Both endpoints are POST: `proposedValue` is arbitrary JSON and
 * doesn't belong in a query string, and `preview` spends a metered ERP
 * call despite being a read, so it isn't cacheable the way a GET implies.
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {
  ErpExpectedFieldChange,
  ErpFieldChange,
  ErpFieldWriteConflict,
  ErpFieldWriteOutcome,
  ErpFieldWritePreview,
  ErpFieldWriteResult,
  ErpValueChangedDetail,
} from '../types';

/* eslint-disable @typescript-eslint/naming-convention */
type RawPreview = {
  field_key: string;
  label: string;
  entity_kind: string;
  external_id: string;
  current_erp_value: unknown;
  current_local_value: unknown;
  proposed_value: unknown;
  is_first_fill: boolean;
  is_no_op: boolean;
};

type RawOutcome = {
  field_key: string;
  label: string;
  written_value: unknown;
  previous_value: unknown;
  was_first_fill: boolean;
};

type RawConflict = {
  field_key: string;
  label: string;
  current_erp_value: unknown;
  expected_current_value: unknown;
  proposed_value: unknown;
};

type RawResult = {
  entity_kind: string;
  external_id: string;
  written: RawOutcome[];
  conflicts: RawConflict[];
  unverified: RawConflict[];
  local_update_failed: boolean;
  local_update_error: string | null;
};

type RawValueChangedDetail = {
  code?: string;
  message?: string;
  current_erp_value?: unknown;
  expected_current_value?: unknown;
  conflicts?: RawConflict[];
};
/* eslint-enable @typescript-eslint/naming-convention */

function convertPreview(raw: RawPreview): ErpFieldWritePreview {
  return {
    fieldKey: raw.field_key,
    label: raw.label,
    entityKind: raw.entity_kind,
    externalId: raw.external_id,
    currentErpValue: raw.current_erp_value,
    currentLocalValue: raw.current_local_value,
    proposedValue: raw.proposed_value,
    isFirstFill: raw.is_first_fill,
    isNoOp: raw.is_no_op,
  };
}

function convertOutcome(raw: RawOutcome): ErpFieldWriteOutcome {
  return {
    fieldKey: raw.field_key,
    label: raw.label,
    writtenValue: raw.written_value,
    previousValue: raw.previous_value,
    wasFirstFill: raw.was_first_fill,
  };
}

function convertConflict(raw: RawConflict): ErpFieldWriteConflict {
  return {
    fieldKey: raw.field_key,
    label: raw.label,
    currentErpValue: raw.current_erp_value,
    expectedCurrentValue: raw.expected_current_value,
    proposedValue: raw.proposed_value,
  };
}

function convertResult(raw: RawResult): ErpFieldWriteResult {
  return {
    entityKind: raw.entity_kind,
    externalId: raw.external_id,
    written: (raw.written ?? []).map((item) => convertOutcome(item)),
    conflicts: (raw.conflicts ?? []).map((item) => convertConflict(item)),
    unverified: (raw.unverified ?? []).map((item) => convertConflict(item)),
    localUpdateFailed: raw.local_update_failed,
    localUpdateError: raw.local_update_error ?? undefined,
  };
}

/**
 * Pull the structured value-changed payload out of a 409, or `undefined`
 * when this isn't that error.
 *
 * FastAPI's `HTTPException(detail=...)` puts the object under `detail`,
 * unlike the ADR error envelope the identity service uses — hence a local
 * reader rather than reusing `features/identity/groups/lib/api-error-helpers`.
 */
export function extractValueChangedDetail(
  error: unknown
): ErpValueChangedDetail | undefined {
  const typed = error as
    | {status?: number; data?: {detail?: RawValueChangedDetail}}
    | undefined;
  if (typed?.status !== 409) return undefined;
  const detail = typed.data?.detail;
  if (detail?.code !== 'erp_value_changed') return undefined;
  return {
    code: 'erp_value_changed',
    message:
      detail.message ??
      'This value changed in Cetec while you were reviewing it.',
    currentErpValue: detail.current_erp_value,
    expectedCurrentValue: detail.expected_current_value,
    conflicts: (detail.conflicts ?? []).map((item) => convertConflict(item)),
  };
}

/** True for the "someone else is already saving this" 409. */
export function isWriteInFlightError(error: unknown): boolean {
  const typed = error as
    | {status?: number; data?: {detail?: RawValueChangedDetail}}
    | undefined;
  return (
    typed?.status === 409 && typed.data?.detail?.code === 'erp_write_in_flight'
  );
}

export class ErpRecordFieldWriteApiClient extends AuthenticatedApiClient {
  private get basePath(): string {
    return '/api/v1/erp/integrations/cetec/record-write';
  }

  /**
   * Diff N fields against the ERP's live values in one metered call.
   *
   * Always a list, even for one field — the manual dialog and the agent's
   * four-field address correction send the same shape, so there is one
   * contract to keep in step rather than two.
   */
  async preview(input: {
    objectRecordId: string;
    changes: ErpFieldChange[];
  }): Promise<ErpFieldWritePreview[]> {
    const raw = await this.request<{items: RawPreview[]}>(
      `${this.basePath}/preview`,
      {
        method: 'POST',
        body: JSON.stringify({
          /* eslint-disable @typescript-eslint/naming-convention */
          object_record_id: input.objectRecordId,
          changes: input.changes.map((change) => ({
            field_key: change.fieldKey,
            proposed_value: change.proposedValue,
          })),
          /* eslint-enable @typescript-eslint/naming-convention */
        }),
      }
    );
    return (raw.items ?? []).map((item) => convertPreview(item));
  }

  /**
   * Write N fields in one PATCH, verified per field.
   *
   * `idempotencyKey` must be generated fresh per *confirm attempt* — reusing
   * one replays the stored outcome of the earlier attempt instead of writing.
   */
  async commit(input: {
    objectRecordId: string;
    changes: ErpExpectedFieldChange[];
    idempotencyKey: string;
  }): Promise<ErpFieldWriteResult> {
    const raw = await this.request<RawResult>(`${this.basePath}/commit`, {
      method: 'POST',
      body: JSON.stringify({
        /* eslint-disable @typescript-eslint/naming-convention */
        object_record_id: input.objectRecordId,
        changes: input.changes.map((change) => ({
          field_key: change.fieldKey,
          proposed_value: change.proposedValue,
          expected_current_value: change.expectedCurrentValue,
        })),
        idempotency_key: input.idempotencyKey,
        /* eslint-enable @typescript-eslint/naming-convention */
      }),
    });
    return convertResult(raw);
  }
}

let cachedErpRecordFieldWriteApi: ErpRecordFieldWriteApiClient | undefined;

export function getErpRecordFieldWriteApi(
  token?: string
): ErpRecordFieldWriteApiClient {
  if (token) return new ErpRecordFieldWriteApiClient(token);
  cachedErpRecordFieldWriteApi ??= new ErpRecordFieldWriteApiClient();
  return cachedErpRecordFieldWriteApi;
}
